import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import ExternalLink from 'lucide-react-native/icons/external-link';
import MapPin from 'lucide-react-native/icons/map-pin';
import Star from 'lucide-react-native/icons/star';
import { ScrollView, StyleSheet, Text, View, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { BoatFacts } from '@/components/detail/boat-facts';
import { BoatCityLink } from '@/components/detail/boat-city-link';
import { BoatHead } from '@/components/detail/boat-head';
import { RelatedList } from '@/components/detail/related-list';
import { FiltersBar } from '@/components/riviera/filters-bar';
import { BoatCard } from '@/components/riviera/boat-card';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { FiltersProvider } from '@/hooks/use-filters';
import { useRelatedBoats } from '@/hooks/use-related';
import { useTheme } from '@/hooks/use-theme';
import { useBoat } from '@todo/core/hooks/use-boat';
import { cities } from '@/lib/events-data';
import { localizedBoatDescription, localizedBoatTitle } from '@/lib/i18n/boat-content';
import { currencySymbol } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { getPrefetched } from '@todo/core/static-prefetch';
import { isBoatDelisted, type Boat } from '@todo/core/boats';

import css from '@/components/detail/detail-desktop.module.css';

export { generateBoatStaticParams as generateStaticParams } from '@todo/core/detail/boat-detail';

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Theme = ReturnType<typeof useTheme>;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return '';
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

function MetaList({
  boat,
  cityName,
  theme,
}: {
  boat: Boat;
  cityName: string | null;
  theme: Theme;
}) {
  return (
    <View style={styles.metaList}>
      {boat.rating != null ? (
        <View style={styles.metaRow}>
          <Star size={16} color={theme.gold} fill={theme.gold} />
          <Text style={[styles.metaText, { color: theme.text }]}>{boat.rating.toFixed(1)}</Text>
        </View>
      ) : null}
      {cityName ? (
        <View style={styles.metaRow}>
          <MapPin size={16} color={theme.gold} />
          <Text style={[styles.metaText, { color: theme.text }]}>{cityName}</Text>
        </View>
      ) : null}
    </View>
  );
}

function PriceRow({ boat, theme, t }: { boat: Boat; theme: Theme; t: Translate }) {
  if (boat.from_price == null) return null;
  return (
    <View style={styles.priceRow}>
      <Text style={[styles.price, { color: theme.gold }]}>
        {formatPrice(boat.from_price, boat.currency, t)}
      </Text>
      <Text style={[styles.perDay, { color: theme.mutedForeground }]}>{t('boats.perDay')}</Text>
    </View>
  );
}

// Shown in place of the price + Book button for a delisted boat (see
// isBoatDelisted) — its booking link is likely dead, so this avoids sending a
// click to it and points at the "more boats" list below instead. Mirrors the
// events screen's EndedNotice.
function DelistedNotice({ theme, t }: { theme: Theme; t: Translate }) {
  return (
    <View style={[styles.delistedNotice, { backgroundColor: theme.muted }]}>
      <Text style={[styles.delistedBanner, { color: theme.text }]}>{t('boats.delistedBanner')}</Text>
      <Text style={[styles.delistedHint, { color: theme.mutedForeground }]}>
        {t('boats.delistedHint')}
      </Text>
    </View>
  );
}

function BookButton({ boat, theme, t }: { boat: Boat; theme: Theme; t: Translate }) {
  const { locale } = useLocale();
  if (!boat.booking_url) return null;
  return (
    <>
      <Button
        variant="gold"
        fullWidth
        style={styles.bookBtn}
        leadingIcon={<ExternalLink size={18} color={theme.goldForeground} />}
        onPress={() =>
          openExternal(boat.booking_url as string, {
            kind: 'boat',
            source: boat.source,
            id: boat.id,
            title: localizedBoatTitle(boat, locale),
            city: boat.city_id,
            locale,
          })
        }>
        {t('boats.book')}
      </Button>
      <Text style={[styles.via, { color: theme.mutedForeground }]}>
        {t('boats.viaClickAndBoat')}
      </Text>
    </>
  );
}

function Hero({
  boat,
  title,
  theme,
  onBack,
  t,
}: {
  boat: Boat;
  title: string;
  theme: Theme;
  onBack: () => void;
  t: Translate;
}) {
  return (
    <>
      {boat.image_url ? (
        <img
          src={boat.image_url}
          alt={title}
          className={css.heroImg}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      ) : (
        <View style={[styles.heroFill, { backgroundColor: theme.muted }]} />
      )}
      <LinearGradient colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']} style={styles.heroScrim} />
      <Pressable
        accessibilityLabel={t('a11y.back')}
        onPress={onBack}
        style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
        <ArrowLeft size={20} color="#fff" />
      </Pressable>
      <View style={styles.heroBadge}>
        <Badge variant="gold">{t('boats.badge')}</Badge>
      </View>
    </>
  );
}

// Web-only: one responsive tree for the mobile and desktop layouts (see the
// single-tree section of detail-desktop.module.css). Head + static params are
// shared with the native file.
export default function BoatScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { boat: fetchedBoat, loading, error } = useBoat(id);

  const boat = fetchedBoat ?? getPrefetched<Boat>('boat', id) ?? null;

  const cityName = boat?.city_id
    ? (cities.find((c) => c.id === boat.city_id)?.name ?? boat.city_id)
    : null;
  const boatTitle = boat ? localizedBoatTitle(boat, locale) : '';
  const bodyText = boat ? localizedBoatDescription(boat, locale) : null;

  const { related, relatedSeedKey, seeAllHref, cityId, hasCityPage, typePage } =
    useRelatedBoats(boat);
  const relatedHeading = cityId
    ? t('boats.moreInCity', { city: cityName })
    : t('boats.more');

  const onBack = () => router.back();

  const description = (t2: Translate, style?: object) =>
    bodyText ? (
      <View style={style}>
        <Text
          role="heading"
          aria-level={2}
          style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
          {t2('boats.aboutBoat')}
        </Text>
        <Text style={[styles.description, { color: theme.textSecondary }]}>{bodyText}</Text>
      </View>
    ) : null;

  return (
    <FiltersProvider>
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        {boat ? (
          <BoatHead
            boat={boat}
            locale={locale}
            related={related}
            relatedSeedKey={relatedSeedKey}
            hasCityPage={hasCityPage}
            typePage={typePage}
          />
        ) : null}
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}>
          {boat ? (
            <>
              {/* One tree for both breakpoints — mobile: full-bleed hero +
                  overlapping card; desktop: filters sidebar + text-forward
                  two-column content. The regrouping is pure CSS
                  (display: contents — see detail-desktop.module.css), so the
                  title, hero, CTA and description are in the HTML once. */}
              <div className={css.shell}>
                <aside className={css.shellSidebar}>
                  {/* Desktop-only sidebar: skip the chip-scroller markup. */}
                  <FiltersBar variant="desktop" />
                </aside>
                <div className={css.shellMain}>
                  <div className={css.detail}>
                    <div className={css.detailMedia}>
                      <Hero boat={boat} title={boatTitle} theme={theme} onBack={onBack} t={t} />
                    </div>

                    <div
                      className={css.detailCard}
                      style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                      <header className={css.detailHeader}>
                        <h1
                          className={css.detailTitle}
                          style={{ color: theme.text, fontFamily: Fonts?.serif }}>
                          {boatTitle}
                        </h1>
                        <MetaList boat={boat} cityName={cityName} theme={theme} />
                      </header>

                      <div
                        className={css.detailBooking}
                        style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                        {isBoatDelisted(boat) ? (
                          <DelistedNotice theme={theme} t={t} />
                        ) : (
                          <>
                            <PriceRow boat={boat} theme={theme} t={t} />
                            <BookButton boat={boat} theme={theme} t={t} />
                          </>
                        )}
                      </div>

                      <section className={css.detailAbout}>
                        {/* Mobile separates the CTA from the body inside the
                            card; on desktop they're in different columns. */}
                        {bodyText ? (
                          <div className={css.mobileOnly}>
                            <View style={[styles.divider, { backgroundColor: theme.border }]} />
                          </div>
                        ) : null}
                        {description(t)}
                        <View style={[styles.divider, { backgroundColor: theme.border }]} />
                        <BoatFacts boat={boat} locale={locale} />
                        {hasCityPage && cityId ? (
                          <View style={styles.faqBox}>
                            <BoatCityLink cityId={cityId} locale={locale} typePage={typePage} />
                          </View>
                        ) : null}
                      </section>
                    </div>
                  </div>
                </div>
              </div>

              {/* "More boats" — bracketed by data-lastmod-skip sentinels so a
                  sibling boat changing never restamps this page's sitemap
                  lastmod (see packages/web-build/src/generate-sitemap.js). */}
              {related.length > 0 ? (
                <>
                  <div data-lastmod-skip="start" hidden />
                  <RelatedList heading={relatedHeading} seeAllHref={seeAllHref}>
                    {related.map((r) => (
                      <BoatCard key={r.id} boat={r} eager={false} />
                    ))}
                  </RelatedList>
                  <div data-lastmod-skip="end" hidden />
                </>
              ) : null}
            </>
          ) : loading ? (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('boats.loadingOne')}
            </Text>
          ) : error ? (
            <Text style={[styles.statusText, { color: theme.destructive }]}>
              {t('boats.loadError', { error })}
            </Text>
          ) : (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('boats.notFound')}
            </Text>
          )}
          <SiteFooter />
        </ScrollView>
      </View>
    </FiltersProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  heroFill: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  metaList: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  metaText: {
    fontSize: 15,
    flex: 1,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: Spacing.two,
  },
  price: {
    fontSize: 22,
    fontWeight: '700',
  },
  perDay: {
    fontSize: 13,
  },
  bookBtn: {
    marginTop: Spacing.three,
  },
  delistedNotice: {
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    gap: 4,
  },
  delistedBanner: {
    fontSize: 15,
    fontWeight: '700',
  },
  delistedHint: {
    fontSize: 13,
  },
  via: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: Spacing.two,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  sectionHeading: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
  faqBox: {
    marginTop: Spacing.five,
  },
});
